import './booking-confirm.scss'
import { Redirect, useParams } from 'react-router'
import { useCallback, useContext, useEffect, useState } from 'react'
import { IBookingStorage } from 'interfaces'
import { bookingService, loggerService, routeService, storageService, stringService } from 'services'
import { LocationDescriptor } from 'history'
import { useHeightListener, usePageTitle } from 'hooks'
import { BackLink, BaseButton, Rating } from 'components'
import {
  IonCol,
  IonGrid,
  IonRow,
  IonIcon,
  IonImg,
  IonItemGroup,
  IonItemDivider,
  IonLabel,
  IonList,
  IonItem,
  IonSpinner,
  IonRadioGroup,
  IonRadio,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { icon_mail, icon_phone } from 'icons'
import BookingSummary from '../../components/booking-summary/booking-summary.component'
import { ThreeBarsBox } from 'components'
import React from 'react'
import { authContext } from 'contexts/'
import { IRequestBooking } from 'interfaces'
import BasePage from 'pages/base-page/base-page.page'

const LOADING_DELAY = Number(process.env.REACT_APP_LOADING_DELAY ?? 2000)

const BookingConfirm: React.FC = () => {
  const auth = useContext(authContext)
  const { uuidBooking } = useParams<any>()
  const [booking, setBooking] = useState<IBookingStorage>()
  const [redirectTo, setRedirectTo] = useState<LocationDescriptor>()

  const { t } = useTranslation()

  const [threeBarsBoxRef, threeBarsBoxHeight] = useHeightListener()

  useEffect(() => {
    if (uuidBooking) {
      const saved_booking = storageService.getSavedBooking(uuidBooking)
      if (saved_booking) {
        setBooking(saved_booking)
      } else setRedirectTo(routeService.generateRoutePath('/')) //non esiste quella prenotazione: imposta il redirect verso la home
    }
  }, [uuidBooking])

  const titlePage = stringService.capitalizeFirstLetter(t('booking_summary'))
  usePageTitle(titlePage!)

  const [isLoading, setIsLoading] = useState(false)
  const [confirm, setConfirm] = useState(false)
  const [isCheck, setIsCheck] = useState(false)
  const confirmClick = useCallback(() => {
    setConfirm(true)
    setIsLoading(true)
  }, [])

  const confirmHandler = useCallback(
    (res: any) => {
      setTimeout(() => {
        setConfirm(false)
        setIsLoading(false)
        storageService.removeBooking(uuidBooking)

        if (!!res.idPrenotazione) {
          setRedirectTo(routeService.generateRoutePath('/booking-success/:uuidBooking', { uuidBooking: res.idPrenotazione }))
        } else {
          loggerService.error('ERROR ON SAVE: ', res)
          setRedirectTo(routeService.generateRoutePath('/error') + '?errorCode=' + res?.response?.data?.codiceErrore)
        }
      }, LOADING_DELAY)
    },
    [uuidBooking],
  )

  useEffect(() => {
    if (!!booking && confirm) {
      const requestBooking: IRequestBooking = {
        requestId: booking.uuid!,
        dataDal: booking.dateFrom!,
        dataAl: booking.dateTo!,
        idStruttura: booking.residence.id!,
        idDisponibilita: booking.guestsPerRoom.map((sr) => sr.room.id.toString()),
        numeroPosti: booking.guestsPerRoom.map((sr) => sr.value),
        codiceEvento: booking.eventCode,
        prezzoTotalePrevisto: booking.totalPrice,
      }
      const controller = bookingService.book(requestBooking, confirmHandler, confirmHandler)
      return () => controller.abort()
    }
  }, [confirm, booking, confirmHandler])

  if (redirectTo) return <Redirect to={redirectTo} />

  const back_link =
    booking &&
    routeService.generateRoutePath('/residence/:residenceId', {
      residenceId: booking.residence.id,
    }) +
      '?' +
      booking.queryParams

  const userData = auth?.getUserData()

  return (
    <BasePage pageClass="edisu-page-booking-confirm" paddingHeight={threeBarsBoxHeight}>
      <IonGrid className="booking-confirm-grid">
        <IonRow className="header-row">
          <IonCol size="12" sizeLg="2">
            {back_link && <BackLink link={back_link} style={{ marginBottom: '30px' }}></BackLink>}
          </IonCol>
          <IonCol size="12" sizeLg="8">
            <h2>{t('confirm_your_prenotation')}</h2>
          </IonCol>
        </IonRow>
        <IonRow className="content-row ion-justify-content-center">
          <IonCol size="12" sizeLg="7">
            <div className="journey-info">
              <p className="edisu-text-big">{t('booking_data')}</p>
              <IonList>
                <IonItemGroup>
                  <IonItemDivider className="ion-no-padding">
                    <IonLabel className="ion-text-uppercase">{t('firstname_and_lastname')}</IonLabel>
                  </IonItemDivider>
                  <IonItem lines="none" className="ion-no-padding">
                    <IonLabel>
                      {userData?.nome} {userData?.cognome}
                    </IonLabel>
                  </IonItem>
                </IonItemGroup>
                <IonItemGroup>
                  <IonItemDivider className="ion-no-padding">
                    <IonLabel className="ion-text-uppercase">{t('personal_data')}</IonLabel>
                  </IonItemDivider>
                  <IonItem lines="none" className="ion-no-padding">
                    <IonIcon icon={icon_mail} slot="start" />
                    <IonLabel>{userData?.email}</IonLabel>
                  </IonItem>
                  {userData?.telefono && (
                    <IonItem lines="none" className="ion-no-padding">
                      <IonIcon icon={icon_phone} slot="start" />
                      <IonLabel>{userData?.telefono}</IonLabel>
                    </IonItem>
                  )}
                </IonItemGroup>
              </IonList>
            </div>
            <div className="residence-info">
              <p className="edisu-text-big">{t('residence_info')}</p>
              <div className="room-info">
                {booking && booking.residence.immagini && booking.residence.immagini.length > 0 && (
                  <IonImg src={booking.residence.immagini[0]} alt="" className="room-image" />
                )}
                <div className="room-info-text">
                  <p className="edisu-text-small">{booking && booking.residence.nome}</p>
                  <p className="edisu-text-small generic-descr">{booking && booking.residence.indirizzo}</p>
                  {booking && <Rating value={booking.residence.gradimento} />}
                </div>
              </div>
              <div className="price-info ion-hide-lg-up">{booking && <BookingSummary booking={booking} />}</div>
            </div>
            <div className="confirm-container">
              {/* TODO <p className="edisu-text-big">{t('booking_confirmation_title')}</p>
              <p className="edisu-text-normal">{t('booking_confirmation_desc')}</p> */}
              <IonLabel className="ion-text-wrap ">
                <IonRadioGroup onIonChange={(e) => (!!e.detail.value ? setIsCheck(true) : setIsCheck(false))} allowEmptySelection={true}>
                  <IonItem lines="none" className="check_item no-hover">
                    <IonRadio value={true}></IonRadio>
                  </IonItem>
                </IonRadioGroup>
                <div>
                  {t('conditions_stay_1')}
                  <a href="/assets/pdf/CondizioniDiSoggiorno.pdf" target="_blank" rel="noopener noreferrer">
                    {t('conditions_stay_2')}
                  </a>
                </div>
              </IonLabel>
              <BaseButton
                extraClassName="book-now-btn"
                label={t('confirm_your_prenotation')}
                onClickFunction={confirmClick}
                disabled={isLoading || !isCheck}
              />
              {isLoading && <IonSpinner name="crescent" color="primary"></IonSpinner>}
            </div>
          </IonCol>
          <IonCol size="12" sizeLg="5" className="ion-hide-lg-down">
            {booking && (
              <ThreeBarsBox nodeRef={threeBarsBoxRef}>
                <BookingSummary booking={booking} />
              </ThreeBarsBox>
            )}
          </IonCol>
        </IonRow>
      </IonGrid>
    </BasePage>
  )
}

export default BookingConfirm
